import { gql } from '@apollo/client';
import { COMPONENT_FRAGMENT, IMPACT_CLAIM_FRAGMENT } from 'graphql/secure/fragments';
import { ACTIVITY_LIST_FRAGMENT } from 'graphql/secure/fragments/activity.fragments';
import { COMPANY_LOG_FRAGMENT } from 'graphql/secure/fragments/company.fragments';
import {
  BASE_PARTNER_COMPANY_FRAGMENT,
  BASE_PARTNER_REQUEST_FRAGMENT,
  PARTNER_COMPANY_SUMMARY_FRAGMENT,
  PARTNER_REQUEST_FRAGMENT,
} from 'graphql/secure/fragments/partner.fragments';
import { BASE_SITE_FRAGMENT } from 'graphql/secure/fragments/site.fragments';
import { IBaseDocument } from 'FeatureModules/Document/types/document.types';
import { IPartnerRequest } from 'FeatureModules/Partner/types/partner.types';
import { IGraphQlConnection } from 'types/core.types';
import { DOCUMENT_FRAGMENT } from '../fragments/document.fragments';

export const GET_ALL_PARTNER_COMPANIES = gql`
  query GetPartnerCompanies(
    $first: Int
    $after: String
    $before: String
    $filters: PartnersFilters
    $orderBy: PartnersOrderBy
  ) {
    partners(first: $first, after: $after, before: $before, filters: $filters, orderBy: $orderBy) {
      edges {
        node {
          ...partnerCompanySummaryValues
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
  ${PARTNER_COMPANY_SUMMARY_FRAGMENT}
`;

export const GET_BASE_PARTNER_COMPANY = gql`
  query GetPartnerCompany($id: UUID!) {
    partner(id: $id) {
      ...basePartnerCompanyValues
    }
  }
  ${BASE_PARTNER_COMPANY_FRAGMENT}
`;

/**
 * @Do not use this query directly, use GET_BASE_PARTNER_COMPANY instead. This is a uge query that is used only in the overview query
 */
export const GET_PARTNER_WITH_COMPANY_IMPACT_CLAIMS = gql`
  query GetPartnerCompany($id: UUID!) {
    partner(id: $id) {
      ...basePartnerCompanyValues
      impactClaims {
        edges {
          node {
            ...impactClaimValues
          }
        }
      }
    }
  }
  ${BASE_PARTNER_COMPANY_FRAGMENT}
  ${IMPACT_CLAIM_FRAGMENT}
`;

export const GET_PARTNER_OVERVIEW = gql`
  query GetPartnerOverview($id: UUID!, $numberOfItems: Int = 3) {
    partner(id: $id) {
      ...basePartnerCompanyValues
      activities(first: $numberOfItems) {
        count
        edges {
          node {
            ...activityListValues
          }
        }
      }
      sites(first: $numberOfItems) {
        count
        edges {
          node {
            ...baseSiteValues
          }
        }
      }
      impactClaims(first: $numberOfItems) {
        count
        edges {
          node {
            ...impactClaimValues
          }
        }
      }

      components(first: $numberOfItems) {
        count
        edges {
          node {
            ...componentValues
          }
        }
      }
      sharedDocuments(first: $numberOfItems) {
        count
        edges {
          node {
            ...documentValues
          }
        }
      }
      requests(first: $numberOfItems) {
        count
        edges {
          node {
            ...partnerRequestValues
          }
        }
      }
      plots {
        count
      }
      logs {
        ...companyLogValues
      }
      latestLog {
        ...companyLogValues
      }
    }
  }
  ${BASE_PARTNER_COMPANY_FRAGMENT}
  ${IMPACT_CLAIM_FRAGMENT}
  ${COMPANY_LOG_FRAGMENT}
  ${ACTIVITY_LIST_FRAGMENT}
  ${BASE_SITE_FRAGMENT}
  ${PARTNER_REQUEST_FRAGMENT}
  ${DOCUMENT_FRAGMENT}
  ${COMPONENT_FRAGMENT}
`;

export interface IGetPartnerUserHomeDataValues {
  documents: IGraphQlConnection<IBaseDocument>;
  partnerRequests: IGraphQlConnection<IPartnerRequest>;
}

export const GET_PARTNER_USER_HOME_DATA = gql`
  query GetPartnerUserHomeData(
    $first: Int!
    $after: String
    $filters: PartnerRequestsFilters
    $orderBy: PartnerRequestsOrderBy
  ) {
    documents(first: $first, after: $after) {
      edges {
        node {
          ...documentValues
        }
      }
    }
    partnerRequests(filters: $filters, orderBy: $orderBy) {
      edges {
        node {
          ...partnerRequestValues
        }
      }
    }
  }
  ${DOCUMENT_FRAGMENT}
  ${PARTNER_REQUEST_FRAGMENT}
`;

export interface IGetPartnerRequestData {
  partnerRequest: IPartnerRequest;
}

export const GET_BASE_PARTNER_REQUEST = gql`
  query GetPartnerRequest($id: UUID!) {
    partnerRequest(id: $id) {
      ...basePartnerRequestValues
    }
  }
  ${BASE_PARTNER_REQUEST_FRAGMENT}
`;

export const GET_PARTNER_REQUEST = gql`
  query GetPartnerRequest($id: UUID!) {
    partnerRequest(id: $id) {
      ...partnerRequestValues
    }
  }
  ${PARTNER_REQUEST_FRAGMENT}
`;

export const GET_ALL_PARTNER_REQUESTS = gql`
  query GetPartnerRequests($filters: PartnerRequestsFilters, $orderBy: PartnerRequestsOrderBy) {
    partnerRequests(filters: $filters, orderBy: $orderBy) {
      count
      edges {
        node {
          ...partnerRequestValues
        }
      }
    }
  }
  ${PARTNER_REQUEST_FRAGMENT}
`;
